/**
 * Accessible Odd Field Out Element
 * Renders a full-width field when there's an odd number of fields in the form.
 * Uses accessible field components with proper ARIA attributes.
 */

import { OddFieldOutPropsType } from '../types';
import AccessibleTextField from './AccessibleTextField';
import AccessibleSelectField from './AccessibleSelectField';
import AccessibleDependentSelectField from './AccessibleDependentSelectField';
import AccessibleTextAreaField from './AccessibleTextAreaField';

interface AccessibleOddFieldOutElementProps {
  oddFieldOutProps: OddFieldOutPropsType;
  requiredFields: string[];
  showErrorMessages?: boolean;
}

/**
 * Renders a full-width field for odd field out scenarios.
 * Delegates to the appropriate accessible field component based on field type.
 *
 * @param oddFieldOutProps - Props for the odd field (includes type, field, classNames, etc.)
 * @param requiredFields - Array of required field parameter names
 * @param showErrorMessages - Whether to show error message text (default: false, shows border only)
 */
const AccessibleOddFieldOutElement = ({
  oddFieldOutProps,
  requiredFields,
  showErrorMessages = false,
}: AccessibleOddFieldOutElementProps) => {
  // If no odd field, render nothing
  if (!oddFieldOutProps) return null;

  const isRequired = requiredFields.includes(
    oddFieldOutProps.field.parameterName,
  );

  switch (oddFieldOutProps.type) {
    case 'SELECT':
      return (
        <AccessibleSelectField
          field={oddFieldOutProps.field}
          placeholder={oddFieldOutProps.placeholder}
          selectOptions={oddFieldOutProps.selectOptions || []}
          selectFieldClassNames={oddFieldOutProps.selectFieldClassNames}
          isTouchedAndError={oddFieldOutProps.isTouchedAndError}
          isRequired={isRequired}
          showErrorMessages={showErrorMessages}
        />
      );

    case 'DEPENDENT_SELECT':
      return (
        <AccessibleDependentSelectField
          field={oddFieldOutProps.field}
          placeholder={oddFieldOutProps.placeholder}
          dependentSelectOptions={oddFieldOutProps.dependentSelectOptions}
          selectFieldClassNames={oddFieldOutProps.selectFieldClassNames}
          isTouchedAndError={oddFieldOutProps.isTouchedAndError}
          isRequired={isRequired}
          showErrorMessages={showErrorMessages}
        />
      );

    case 'TEXTAREA':
      return (
        <AccessibleTextAreaField
          type={oddFieldOutProps.type}
          field={oddFieldOutProps.field}
          placeholder={oddFieldOutProps.placeholder}
          textFieldClassNames={oddFieldOutProps.textFieldClassNames}
          isTouchedAndError={oddFieldOutProps.isTouchedAndError}
          isRequired={isRequired}
          showErrorMessages={showErrorMessages}
        />
      );

    case 'TEXT':
    default:
      return (
        <AccessibleTextField
          type={oddFieldOutProps.type || 'TEXT'}
          field={oddFieldOutProps.field}
          placeholder={oddFieldOutProps.placeholder}
          textFieldClassNames={oddFieldOutProps.textFieldClassNames}
          isTouchedAndError={oddFieldOutProps.isTouchedAndError}
          isRequired={isRequired}
          showErrorMessages={showErrorMessages}
        />
      );
  }
};

export default AccessibleOddFieldOutElement;
